import React, { useState, useRef, useEffect } from 'react';
import { X, Plus, SquarePen, ChevronDown, Link2, ChevronLeft, CreditCard, Lock, Trash2, LogOut, ArrowRight, Check, Loader2, Package, Star, Sparkles, MapPin, Phone, Smartphone, CheckCircle } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { useNotification } from './Notification';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  isDark: boolean;
  userAvatar: string;
  userName: string;
  onLogout: () => void;
  userEmail?: string;
  userPhone?: string;
}

type ProfileView = 'main' | 'orders' | 'settings';

export const ProfileModal: React.FC<ProfileModalProps> = ({
  isOpen,
  onClose,
  isDark,
  userAvatar,
  userName,
  onLogout,
  userEmail,
  userPhone
}) => {
  const { updateProfileName, updateProfilePhoto, updateProfileDetails, profile, user } = useUser();
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  // States
  const [profileView, setProfileView] = useState<ProfileView>('main');
  const [orders, setOrders] = useState<any[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [isLinkAccountOpen, setIsLinkAccountOpen] = useState(false);
  const [isPaymentMethodsOpen, setIsPaymentMethodsOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewForm, setReviewForm] = useState<{ rating: number, comment: string, anonymous: boolean, itemId: string | number }>({
    rating: 5,
    comment: '',
    anonymous: false,
    itemId: ''
  });

  // Editing State
  const [isEditingName, setIsEditingName] = useState(false);
  const [newName, setNewName] = useState(userName);
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [details, setDetails] = useState({
    shippingAddress: profile?.shippingAddress || '',
    shippingPhone: profile?.shippingPhone || '',
    vodafoneCashNumber: profile?.vodafoneCashNumber || ''
  });
  // New Features State
  const [returnFormOpen, setReturnFormOpen] = useState(false);
  const [returnItems, setReturnItems] = useState<{ id: string | number, selected: boolean, reason: string }[]>([]);
  const [isSubmittingReturn, setIsSubmittingReturn] = useState(false);

  // Review System
  const [reviewFormOpen, setReviewFormOpen] = useState(false);
  const [reviewData, setReviewData] = useState<{ rating: number, comment: string, anonymous: boolean, itemId: string | number, productId: string | number, shopId?: string | number, productName?: string }>({
    rating: 5,
    comment: '',
    anonymous: false,
    itemId: '',
    productId: ''
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Effects
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  // Handle browser back button for profile views
  useEffect(() => {
    const handlePopHistory = (event: PopStateEvent) => {
      if (!isOpen) return;

      if (selectedOrder) {
        setSelectedOrder(null);
      } else if (profileView !== 'main') {
        setProfileView('main');
      } else {
        // Only call onClose if we are at the main profile view and the user presses back
        onClose();
      }
    };

    window.addEventListener('popstate', handlePopHistory);
    return () => window.removeEventListener('popstate', handlePopHistory);
  }, [isOpen, selectedOrder, profileView, onClose]);
  useEffect(() => {
    if (profile) {
      setDetails({
        shippingAddress: profile.shippingAddress || '',
        shippingPhone: profile.phone || profile.shippingPhone || '',
        vodafoneCashNumber: profile.vodafoneCashNumber || ''
      });
    }
  }, [profile]);

  if (!isOpen) return null;

  const closeProfile = () => {
    onClose();
    setIsEditingName(false);
    setTimeout(() => setProfileView('main'), 300);
  };

  const goToOrders = async () => {
    setProfileView('orders');
    if (orders.length === 0) {
      setLoadingOrders(true);
      try {
        const { collection, query, where, getDocs, db } = await import('../firebase');
        const user = auth.currentUser;
        if (user) {
          const q = query(collection(db, 'orders'), where('userId', '==', user.uid));
          const snap = await getDocs(q);
          const list: any[] = [];
          snap.forEach(doc => list.push({ id: doc.id, ...doc.data() }));
          list.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
          setOrders(list);
        }
      } catch (err) {
        console.error("Error fetching orders:", err);
      } finally {
        setLoadingOrders(false);
      }
    }
  };

  const handleBack = () => {
    if (selectedOrder) {
      setSelectedOrder(null);
    } else {
      setProfileView('main');
    }
  };

  const goToMainProfile = () => {
    setProfileView('main');
    setSelectedOrder(null);
  };

  const goToSettings = () => {
    setProfileView('settings');
  };

  const handleSupport = (order?: any) => {
    const whatsappNumber = "201202796965";
    let message = "";

    if (order && order.id !== 'GENERAL_SUPPORT') {
      const itemsList = order.items.map((i: any) => `- ${i.name} (x${i.quantity})`).join('\n');
      message = `مرحباً، لدي مشكلة في الطلب رقم #${order.id.slice(0, 8)}
حالة الطلب الحالية: ${order.status}
إجمالي المبلغ: ${order.totalAmount} EGP
المنتجات:
${itemsList}

بيانات الحساب:
الاسم: ${userName}
الهاتف: ${details.shippingPhone || userPhone || 'غير متوفر'}
البريد: ${userEmail || 'غير متوفر'}

أنا لدي مشكلة في هذا الطلب، يرجى المساعدة.`;
    } else {
      message = `مرحباً، أريد الاستفسار عن خدمة العملاء.
بيانات الحساب:
الاسم: ${userName}
الهاتف: ${details.shippingPhone || userPhone || 'غير متوفر'}
البريد: ${userEmail || 'غير متوفر'}

لدي مشكلة وأحتاج للمساعدة.`;
    }

    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${whatsappNumber}?text=${encodedMessage}`, '_blank');
  };

  const handleSaveName = async () => {
    if (!newName.trim()) return;
    try {
      await updateProfileName(newName);
      setIsEditingName(false);
    } catch (error) {
      console.error("Error updating name:", error);
      showNotification("حدث خطأ أثناء تحديث الاسم", "error");
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        showNotification('يرجى اختيار ملف صورة صحيح (JPEG, PNG, etc.)', 'warning');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showNotification('حجم الصورة كبير جداً. الحد الأقصى هو 5 ميجابايت.', 'warning');
        return;
      }
      setIsUploading(true);
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result as string;
        try {
          await updateProfilePhoto(base64String);
        } catch (error: any) {
          console.error("Error updating photo:", error);
          showNotification(error.message || "حدث خطأ أثناء تحديث الصورة", "error");
        } finally {
          setIsUploading(false);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileInput = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  const handleLinkGoogle = async () => {
    setIsLinking(true);
    try {
      const { GoogleAuthProvider, linkWithPopup } = await import('firebase/auth');
      const provider = new GoogleAuthProvider();
      await linkWithPopup(auth.currentUser!, provider);
      showNotification("تم ربط الحساب بنجاح!", "success");
    } catch (error: any) {
      console.error("Error linking account:", error);
      showNotification(error.message || "حدث خطأ أثناء ربط الحساب", "error");
    } finally {
      setIsLinking(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      const { db, doc, updateDoc, serverTimestamp } = await import('../firebase');
      const user = auth.currentUser;
      if (user) {
        await updateDoc(doc(db, 'users', user.uid), {
          status: 'deleted',
          deletedAt: serverTimestamp()
        });
        await onLogout();
        setShowDeleteConfirm(false);
        showNotification("تم إرسال طلب حذف الحساب بنجاح. سيتم مراجعة طلبك.", "success");
      }
    } catch (error: any) {
      console.error("Error deleting account:", error);
      showNotification("حدث خطأ أثناء حذف الحساب", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSaveDetails = async () => {
    setIsSavingDetails(true);
    try {
      await updateProfileDetails(details);
      showNotification("تم حفظ البيانات بنجاح!", "success");
    } catch (error) {
      console.error("Error saving details:", error);
      showNotification("حدث خطأ أثناء حفظ البيانات", "error");
    } finally {
      setIsSavingDetails(false);
    }
  };

  const openReturnForm = (order: any) => {
    setReturnItems(order.items.map((i: any) => ({
      id: i.id || i.product?.id || Math.random().toString(),
      selected: false,
      reason: ''
    })));
    setReturnFormOpen(true);
  };

  const handleSubmitReturn = async () => {
    const selected = returnItems.filter(i => i.selected);
    if (selected.length === 0) {
      showNotification('يرجى تحديد عنصر واحد على الأقل للاسترجاع', 'warning');
      return;
    }
    if (selected.some(i => !i.reason.trim())) {
      showNotification('يرجى كتابة سبب الاسترجاع للعناصر المحددة', 'warning');
      return;
    }

    setIsSubmittingReturn(true);
    try {
      const { db, doc, updateDoc } = await import('../firebase');
      // Create the updated items array marking returns
      const updatedItems = selectedOrder.items.map((item: any) => {
        const matchingReturn = selected.find(r => r.id === (item.id || item.product?.id));
        if (matchingReturn) {
          return {
            ...item,
            returnStatus: 'pending',
            returnReason: matchingReturn.reason
          };
        }
        return item;
      });

      await updateDoc(doc(db, 'orders', selectedOrder.id), {
        items: updatedItems,
        hasReturnRequest: true
      });

      // Update local state
      const newOrders = orders.map(o => o.id === selectedOrder.id ? { ...o, items: updatedItems, hasReturnRequest: true } : o);
      setOrders(newOrders);
      setSelectedOrder(newOrders.find(o => o.id === selectedOrder.id));

      showNotification('تم إرسال طلب الاسترجاع بنجاح. سيتم مراجعة طلبك!', 'success');
      setReturnFormOpen(false);
    } catch (err) {
      console.error(err);
      showNotification('حدث خطأ أثناء تقديم الطلب', 'error');
    } finally {
      setIsSubmittingReturn(false);
    }
  };

  const openReviewForm = (item: any) => {
    setReviewData({
      rating: 5,
      comment: '',
      anonymous: false,
      itemId: item.id || item.product?.id || Math.random().toString(),
      productId: item.id || item.product?.id, // Product id in db
      shopId: item.storeId || item.product?.storeId,
      productName: item.name
    });
    setReviewFormOpen(true);
  };

  const handleSubmitReview = async () => {
    if (!reviewData.comment.trim()) {
      showNotification('يرجى كتابة تقييمك أولاً', 'warning');
      return;
    }

    setSubmittingReview(true);
    try {
      const { db, collection, addDoc, doc, updateDoc, increment, serverTimestamp, getDoc } = await import('../firebase');

      // 1. Save Review to tracking collection
      await addDoc(collection(db, 'reviews'), {
        productId: String(reviewData.productId),
        shopId: reviewData.shopId ? String(reviewData.shopId) : null,
        orderId: selectedOrder.id,
        userId: user?.uid || 'anonymous',
        userName: reviewData.anonymous ? 'فاعل خير' : userName,
        userPhoto: reviewData.anonymous ? '' : userAvatar,
        isAnonymous: reviewData.anonymous,
        rating: reviewData.rating,
        comment: reviewData.comment,
        createdAt: serverTimestamp()
      });

      // 2. Update Product document stats (optimistic rating handling)
      const prodRef = doc(db, 'products', String(reviewData.productId));
      const prodSnap = await getDoc(prodRef);
      if (prodSnap.exists()) {
        const pData = prodSnap.data();
        const currentCount = pData.reviewCount || 0;
        const currentRating = pData.rating || 5;
        // Formula: New Average = (Old Average * Old Count + New Rating) / (Old Count + 1)
        const newRating = ((currentRating * currentCount) + reviewData.rating) / (currentCount + 1);

        await updateDoc(prodRef, {
          rating: newRating,
          reviewCount: increment(1)
        });
      }

      showNotification('تم إرسال تقييمك للمنتج بنجاح، شكراً لك!', 'success');
      setReviewFormOpen(false);
    } catch (err) {
      console.error(err);
      showNotification('حدث خطأ أثناء محاولة إرسال التقييم', 'error');
    } finally {
      setSubmittingReview(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-[6000] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-md animate-fade-in" onClick={closeProfile}></div>
        <div className={`relative w-full max-w-[520px] max-h-[90vh] rounded-[2.5rem] shadow-2xl border ${isDark ? 'bg-[#121418] border-white/10 text-white' : 'bg-[#fdfdff] border-white text-slate-800'} backdrop-blur-3xl flex flex-col items-center animate-profile-bloom overflow-hidden`}>

          {profileView === 'main' ? (
            <div className="w-full shrink-0 flex flex-col h-full max-h-[90vh] animate-fade-in">
              <div className="w-full flex flex-col items-center px-6 pt-10 pb-4 shrink-0 relative">
                <button onClick={closeProfile} className="absolute top-4 right-4 p-2 rounded-full bg-red-100/50 md:bg-red-100 text-red-500 hover:bg-red-200 transition-all hover:rotate-90 active:scale-90 shadow-sm z-10"><X size={22} strokeWidth={2.5} /></button>

                <div className="relative mt-2 mb-3 flex flex-col items-center">
                  <div
                    className={`w-28 h-28 rounded-full overflow-hidden border-4 ${isDark ? 'border-white/10' : 'border-white'} shadow-xl transform transition-transform duration-500 cursor-pointer hover:scale-105 ${isUploading ? 'opacity-70' : ''}`}
                    onClick={() => { if (!isUploading && userAvatar) setIsPreviewOpen(true); }}
                  >
                    <img src={userAvatar} alt="Profile" className="w-full h-full object-cover" />
                    {isUploading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 backdrop-blur-sm">
                        <Loader2 size={32} className="text-white animate-spin" />
                      </div>
                    )}
                  </div>
                  <button onClick={triggerFileInput} className="absolute bottom-0 right-0 p-2.5 rounded-2xl bg-blue-500 text-white shadow-lg hover:bg-blue-600 transition-all hover:scale-110 active:scale-90 border-4 border-white dark:border-[#121418]"><SquarePen size={18} /></button>
                  <input type="file" ref={fileInputRef} onChange={handleImageUpload} className="hidden" accept="image/*" />
                </div>

                <div className="text-center w-full px-4">
                  {isEditingName ? (
                    <div className="flex items-center justify-center gap-2 animate-fade-in">
                      <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} className={`w-full max-w-[240px] px-4 py-2 rounded-xl text-center font-black outline-none focus:ring-2 focus:ring-blue-500/20 border ${isDark ? 'bg-black/20 border-white/10 text-white' : 'bg-slate-50 border-slate-200 text-slate-800'}`} autoFocus onKeyDown={(e) => e.key === 'Enter' && handleSaveName()} />
                      <button onClick={handleSaveName} className="p-2.5 rounded-xl bg-green-500 text-white shadow-lg hover:bg-green-600 transition-all"><Check size={20} /></button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2 group cursor-pointer" onClick={() => setIsEditingName(true)}>
                      <h2 className="text-2xl sm:text-3xl font-black">{userName}</h2>
                      <SquarePen size={16} className="text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  )}
                  <p className={`text-xs sm:text-sm font-bold mt-1 opacity-60 ${isDark ? 'text-white' : 'text-slate-500'}`}>{userEmail || 'تسجيل دخول مجهول'}</p>
                </div>
                <div className="mt-4 flex flex-wrap justify-center gap-2">
                  <span className={`px-4 py-1.5 rounded-full text-[10px] sm:text-xs font-black bg-blue-500/10 text-blue-500 border border-blue-500/20 flex items-center gap-2`}>
                    <Sparkles size={12} className="animate-pulse" /> رصيد نقاط الهدايا: {profile?.pointsBalance || 0}
                  </span>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto custom-scrollbar px-6 sm:px-10 pb-6 space-y-4" style={{ direction: 'rtl' }}>
                <div className="space-y-2">
                  <div onClick={() => setIsLinkAccountOpen(!isLinkAccountOpen)} className={`flex items-center justify-between p-4 rounded-[2rem] border transition-all cursor-pointer group/item ${isDark ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-white border-slate-100 shadow-sm hover:shadow-md'} ${isLinkAccountOpen ? 'ring-2 ring-blue-400/30' : ''}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center bg-[#f1f5f9] text-slate-400`}><Link2 size={24} /></div>
                      <div className="text-right"><p className="font-black text-lg text-slate-800 dark:text-white">ربط الحساب</p><p className="text-[11px] font-bold text-slate-400">اربط حساباتك الاجتماعية</p></div>
                    </div>
                    <ChevronDown size={22} className={`transition-transform duration-500 ${isLinkAccountOpen ? 'rotate-180' : ''} text-slate-300`} />
                  </div>
                  {isLinkAccountOpen && (
                    <div className="px-4 py-2 animate-fade-in space-y-4">
                      {(() => {
                        const googleLinked = user?.providerData.find(p => p.providerId === 'google.com');
                        if (googleLinked) {
                          return (
                            <div className={`w-full p-4 rounded-3xl flex flex-col items-center justify-center space-y-2 shadow-lg border ${isDark ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`}>
                              <div className="flex items-center gap-2"><CheckCircle size={20} /><span className="font-black text-sm">تم ربط حساب جوجل بنجاح ✅</span></div>
                              <span className="text-xs font-bold opacity-80">{googleLinked.email}</span>
                            </div>
                          );
                        }
                        return (
                          <button onClick={handleLinkGoogle} disabled={isLinking} className={`w-full py-4 rounded-3xl flex items-center justify-center gap-3 shadow-lg border ${isDark ? 'bg-white/10 border-white/20 text-white hover:bg-white/20' : 'bg-white border-slate-200 hover:bg-slate-50'}`}>
                            {isLinking ? <Loader2 size={18} className="animate-spin text-blue-500" /> : <img src="https://www.gstatic.com/images/branding/product/1x/googleg_48dp.png" alt="" className="w-6 h-6" />}
                            <span className="font-black">ربط حساب (Gmail) جوجل</span>
                          </button>
                        );
                      })()}
                    </div>
                  )}
                </div>

                <div onClick={goToSettings} className={`flex items-center justify-between p-4 rounded-[2rem] border transition-all cursor-pointer group/item ${isDark ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-white border-slate-100 shadow-sm hover:shadow-md'}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl flex items-center justify-center bg-[#dcfce7] text-[#10b981]"><CreditCard size={24} /></div>
                    <div className="text-right"><p className="font-black text-lg">معلومات الحساب</p><p className="text-[11px] font-bold text-slate-400">تلقائية العنوان وأرقام التواصل</p></div>
                  </div>
                  <ChevronLeft size={20} className="text-slate-300" />
                </div>

                <div onClick={goToOrders} className={`flex items-center justify-between p-4 rounded-[2rem] border transition-all cursor-pointer group/item ${isDark ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-white border-slate-100 shadow-sm hover:shadow-md'}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl flex items-center justify-center bg-[#e0e7ff] text-[#6366f1]"><Package size={22} /></div>
                    <div className="text-right"><p className="font-black text-lg">طلباتي السابقة</p><p className="text-[11px] font-bold text-slate-400">عرض سجل مشترياتك وتقييم المنتجات</p></div>
                  </div>
                  <ChevronLeft size={20} className="text-slate-300" />
                </div>

                <div onClick={() => setShowDeleteConfirm(true)} className={`flex items-center justify-between p-4 rounded-[2rem] border transition-all cursor-pointer hover:bg-red-50 group/item ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-slate-100'}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl flex items-center justify-center bg-[#fee2e2] text-[#ef4444]"><Trash2 size={22} /></div>
                    <div className="text-right"><p className="font-black text-lg text-red-500">حذف الحساب نهائياً</p><p className="text-[11px] font-bold text-red-400">سيتم حذف جميع بياناتك</p></div>
                  </div>
                  <ChevronLeft size={20} className="text-red-300" />
                </div>

                <div onClick={() => handleSupport({ id: 'GENERAL_SUPPORT' })} className={`flex items-center justify-between p-4 rounded-[2rem] border transition-all cursor-pointer bg-[#25D366]/10 hover:bg-[#25D366]/20 border-[#25D366]/20 group/item`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl flex items-center justify-center bg-[#25D366] text-white"><Phone size={22} /></div>
                    <div className="text-right"><p className="font-black text-lg text-[#25D366]">دردشة مع خدمة العملاء</p><p className="text-[11px] font-bold text-[#25D366]/70">تواصل معنا عبر واتساب للمساعدة</p></div>
                  </div>
                  <ChevronLeft size={20} className="text-[#25D366]" />
                </div>

                <div onClick={() => setShowLogoutConfirm(true)} className={`flex items-center justify-between p-4 rounded-[2rem] border transition-all cursor-pointer hover:bg-orange-50 group/item ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-slate-100'}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl flex items-center justify-center bg-orange-100 text-orange-500"><LogOut size={22} className="rotate-180" /></div>
                    <div className="text-right"><p className="font-black text-lg text-orange-500">تسجيل الخروج</p><p className="text-[11px] font-bold text-orange-400">الخروج من الحساب الحالي</p></div>
                  </div>
                  <ChevronLeft size={20} className="text-orange-300" />
                </div>
              </div>
            </div>
          ) : profileView === 'settings' ? (
            <div className="w-full flex flex-col h-full animate-fade-in" style={{ direction: 'rtl' }}>
              <div className="shrink-0 flex items-center justify-between p-6">
                <button onClick={handleBack} className="p-2 rounded-full bg-slate-200 dark:bg-white/10"><ArrowRight size={22} /></button>
                <h3 className="text-2xl font-black">معلومات الحساب</h3>
                <div className="w-8"></div>
              </div>
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                <div className={`p-6 rounded-[2rem] border ${isDark ? 'bg-white/5 border-white/5' : 'bg-slate-50 border-slate-200'} space-y-5`}>
                  <div className="space-y-2"><label className="text-xs font-black text-blue-500 flex items-center gap-2"><MapPin size={14} />عنوان التوصيل الرئيسي</label><input type="text" className={`w-full p-4 rounded-2xl border outline-none font-bold ${isDark ? 'bg-black/20 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-700'}`} value={details.shippingAddress} onChange={(e) => setDetails({ ...details, shippingAddress: e.target.value })} /></div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2"><label className="text-xs font-black text-blue-500 flex items-center gap-2"><Phone size={14} />رقم الهاتف</label><input type="tel" className={`w-full p-4 rounded-2xl border outline-none font-bold text-center ${isDark ? 'bg-black/20 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-700'}`} value={details.shippingPhone} onChange={(e) => setDetails({ ...details, shippingPhone: e.target.value })} /></div>
                    <div className="space-y-2"><label className="text-xs font-black text-purple-500 flex items-center gap-2"><Smartphone size={14} />رقم فودافون كاش</label><input type="tel" className={`w-full p-4 rounded-2xl border outline-none font-bold text-center ${isDark ? 'bg-black/20 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-700'}`} value={details.vodafoneCashNumber} onChange={(e) => setDetails({ ...details, vodafoneCashNumber: e.target.value })} /></div>
                  </div>
                  <button onClick={handleSaveDetails} disabled={isSavingDetails} className="w-full py-4 rounded-2xl font-black bg-emerald-500 text-white hover:bg-emerald-600 shadow-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50">{isSavingDetails ? <Loader2 size={20} className="animate-spin" /> : <Check size={20} />}حفظ البيانات</button>
                </div>
              </div>
            </div>
          ) : (
            <div className="w-full flex flex-col h-full animate-fade-in" style={{ direction: 'rtl' }}>
              <div className="shrink-0 flex items-center justify-between p-6">
                <button onClick={handleBack} className="p-2 rounded-full bg-slate-200 dark:bg-white/10"><ArrowRight size={22} /></button>
                <h3 className="text-2xl font-black">{selectedOrder ? 'تفاصيل الطلب' : 'طلباتي السابقة'}</h3>
                <div className="w-8"></div>
              </div>
              <div className="flex-1 overflow-y-auto p-6 pt-0 custom-scrollbar">
                {!selectedOrder ? (
                  <div className="space-y-4">
                    {/* Cancellation / Gmail Verification Warning */}
                    {orders.some(o => o.status === 'cancelled') && (
                      <div className={`p-4 rounded-3xl border animate-pulse flex flex-col items-center text-center gap-2 ${isDark ? 'bg-red-500/10 border-red-500/20 text-red-400' : 'bg-red-50 border-red-100 text-red-600'}`}>
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isDark ? 'bg-red-500/20' : 'bg-red-100'}`}>
                          <img src="https://github.com/yousefkatato11-creator/main/blob/main/694eb8fe-2fdd-458e-aa8e-57969c337e81_20260204_031435_%D9%A0%D9%A0%D9%A0%D9%A0%20(1).png?raw=true" alt="Logo" className="w-8 h-8 object-contain" />
                        </div>
                        <p className="font-black text-sm">يرجى تأكيد جيميلك في أقرب وقت لتفعيل حسابك وإتمام الطلبات القادمة بنجاح.</p>
                      </div>
                    )}
                    {loadingOrders ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-blue-500" size={32} /></div> : orders.length === 0 ? <p className="text-center py-10 font-bold opacity-40">لا يوجد طلبات سابقة بعد</p> : orders.map(order => (
                      <div key={order.id} onClick={() => setSelectedOrder(order)} className={`p-5 rounded-[2rem] border transition-all cursor-pointer hover:scale-[1.02] ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-slate-100 shadow-sm'}`}>
                        <div className="flex justify-between items-center mb-2"><span className="font-black text-blue-500">#{order.id.slice(0, 8)}</span><span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase ${order.status === 'delivered' ? 'bg-green-100 text-green-600' : order.status === 'cancelled' ? 'bg-red-100 text-red-600' : 'bg-yellow-100 text-yellow-600'}`}>{order.status}</span></div>
                        <div className="flex justify-between items-center text-sm"><span className="opacity-60">{new Date(order.date).toLocaleDateString('ar-EG')}</span><span className="font-black">{order.totalAmount} EGP</span></div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className={`p-4 rounded-3xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-100'}`}>
                      <div className="space-y-4">
                        {selectedOrder.items.map((item: any, idx: number) => (
                          <div key={idx} className={`flex gap-4 ${item.isCancelled ? 'opacity-50' : ''}`}>
                            <div
                              onClick={() => { const prodId = item.productId || String(item.id).split('_')[0]; if (prodId) { onClose(); navigate(`/product/${prodId}`); } }}
                              className="w-16 h-16 rounded-2xl bg-white overflow-hidden border shrink-0 cursor-pointer hover:shadow-md transition-shadow active:scale-95"
                            >
                              <img src={item.image} alt="" className="w-full h-full object-contain" />
                              {item.isCancelled && <div className="absolute inset-0 bg-red-500/20 flex items-center justify-center pointer-events-none"><span className="bg-red-500 text-white text-[8px] px-1 rounded">CANCELLED</span></div>}
                            </div>
                            <div className="flex-1 flex flex-col justify-center py-1">
                              <p
                                onClick={() => { const prodId = item.productId || String(item.id).split('_')[0]; if (prodId) { onClose(); navigate(`/product/${prodId}`); } }}
                                className="font-black text-sm flex items-center gap-2 cursor-pointer hover:text-blue-500 transition-colors w-fit"
                              >
                                {item.name}
                                {item.isCancelled && <span className="text-[10px] text-red-500 font-bold">(ملغي)</span>}
                              </p>
                              <span className="text-xs font-bold opacity-40">{item.quantity} × {item.price} EGP</span>
                              {item.isCancelled && item.cancelReason && (
                                <p className="text-[10px] text-red-500 font-bold mt-1">السبب: {item.cancelReason}</p>
                              )}
                              {item.returnStatus === 'pending' && (
                                <p className="text-[10px] text-orange-500 font-bold mt-1">⏳ جاري مراجعة طلب الاسترجاع</p>
                              )}
                              {item.returnStatus === 'approved' && (
                                <p className="text-[10px] text-green-500 font-bold mt-1">✅ تم الموافقة على الاسترجاع</p>
                              )}
                            </div>
                            <div className="flex flex-col items-center justify-center">
                              {!item.isCancelled && item.returnStatus !== 'pending' && item.returnStatus !== 'approved' && selectedOrder.status === 'delivered' && (
                                <button onClick={(e) => { e.stopPropagation(); openReviewForm(item); }} className="px-3 py-1.5 rounded-lg text-xs font-black bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-500/10 dark:text-blue-400 dark:hover:bg-blue-500/20 transition-all flex items-center gap-1 border border-blue-200 dark:border-blue-500/20 mb-2">
                                  <Star size={12} className="fill-blue-500 text-blue-500" />
                                  تقييم المنتج
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {selectedOrder.status === 'cancelled' && selectedOrder.cancelReason && (
                      <div className="p-4 rounded-3xl bg-red-50 dark:bg-red-500/10 border border-red-100 dark:border-red-500/20">
                        <p className="text-xs font-black text-red-500 mb-1">سبب إلغاء الطلب:</p>
                        <p className="text-sm font-bold opacity-80">{selectedOrder.cancelReason}</p>
                      </div>
                    )}
                    {selectedOrder.orderNotes && (
                      <div className="p-4 rounded-3xl bg-blue-50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-500/20">
                        <p className="text-xs font-black text-blue-500 mb-1">ملاحظاتك للطلب:</p>
                        <p className="text-sm font-bold opacity-80 whitespace-pre-wrap">{selectedOrder.orderNotes}</p>
                      </div>
                    )}
                    <div className="mt-4 pt-4 border-t border-slate-200/50 space-y-3 text-sm font-bold opacity-70">
                      <div className="flex justify-between"><span>الإجمالي:</span><span className="text-xl font-black text-blue-500">{selectedOrder.totalAmount} EGP</span></div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 pt-4">
                      <button
                        onClick={() => handleSupport(selectedOrder)}
                        className="flex-1 py-4 rounded-2xl font-black bg-[#25D366] text-white hover:bg-[#128C7E] shadow-xl transition-all flex items-center justify-center gap-2"
                      >
                        <Phone size={20} />
                        تواصل مع الدعم
                      </button>

                      {selectedOrder.status === 'delivered' && !selectedOrder.hasReturnRequest && (
                        <button
                          onClick={() => openReturnForm(selectedOrder)}
                          className="flex-1 py-4 rounded-2xl font-black border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-white dark:hover:bg-white/10 shadow-sm transition-all flex items-center justify-center gap-2"
                        >
                          <svg className="w-5 h-5 opacity-80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" /></svg>
                          طلب استرجاع
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div >

      {isPreviewOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 backdrop-blur-xl animate-fade-in" onClick={() => setIsPreviewOpen(false)}>
          <button onClick={() => setIsPreviewOpen(false)} className="absolute top-6 right-6 p-3 rounded-full bg-white/10 text-white"><X size={24} /></button>
          <img src={userAvatar} alt="Profile" className="max-w-[90vw] max-h-[90vh] object-contain rounded-2xl shadow-2xl animate-scale-in" />
        </div>
      )
      }

      {
        showLogoutConfirm && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setShowLogoutConfirm(false)}></div>
            <div className={`relative p-8 rounded-[2.5rem] w-full max-w-[400px] text-center space-y-6 ${isDark ? 'bg-[#1a1c22] text-white border-white/10' : 'bg-white text-slate-800'}`}>
              <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto"><LogOut size={32} className="text-orange-500 rotate-180" /></div>
              <div className="space-y-2"><h3 className="text-2xl font-black">تسجيل الخروج؟</h3><p className="font-bold opacity-60">هل أنت متأكد أنك تريد الخروج من حسابك؟</p></div>
              <div className="flex gap-4">
                <button onClick={() => setShowLogoutConfirm(false)} className={`flex-1 py-4 font-black rounded-2xl border transition-all active:scale-95 ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-slate-200 hover:bg-slate-50'}`}>إلغاء</button>
                <button onClick={onLogout} className="flex-1 py-4 font-black rounded-2xl bg-red-500 text-white hover:bg-red-600 shadow-lg shadow-red-500/20 transition-all active:scale-95">تأكيد الخروج</button>
              </div>
            </div>
          </div>
        )
      }

      {
        showDeleteConfirm && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setShowDeleteConfirm(false)}></div>
            <div className={`relative p-8 rounded-[2.5rem] w-full max-w-[400px] text-center space-y-6 ${isDark ? 'bg-[#1a1c22] text-white border-white/10' : 'bg-white text-slate-800'}`}>
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto"><Trash2 size={32} className="text-red-500" /></div>
              <div className="space-y-2"><h3 className="text-2xl font-black">حذف الحساب؟</h3><p className="font-bold opacity-60 text-red-500">سيتم حذف بياناتك وتوقف حسابك. هل أنت متأكد؟</p></div>
              <div className="flex gap-4"><button onClick={() => setShowDeleteConfirm(false)} className="flex-1 py-4 font-black rounded-2xl border border-slate-200 dark:border-white/10">إلغاء</button><button onClick={handleDeleteAccount} disabled={isDeleting} className="flex-1 py-4 font-black rounded-2xl bg-red-500 text-white disabled:opacity-50">{isDeleting ? <Loader2 className="animate-spin mx-auto" size={24} /> : 'تأكيد الحذف'}</button></div>
            </div>
          </div>
        )
      }
      {
        returnFormOpen && selectedOrder && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setReturnFormOpen(false)}></div>
            <div className={`relative p-6 sm:p-8 rounded-[2.5rem] w-full max-w-[500px] space-y-6 ${isDark ? 'bg-[#1a1c22] text-white border border-white/10' : 'bg-white text-slate-800'}`}>
              <div className="flex justify-between items-center border-b border-slate-200/50 dark:border-white/10 pb-4">
                <h3 className="text-xl font-black text-slate-800 dark:text-white">تفاصيل استرجاع الطلب</h3>
                <button onClick={() => setReturnFormOpen(false)} className={`p-2 rounded-full ${isDark ? 'bg-white/5 text-white/60 hover:text-white' : 'bg-slate-50 text-slate-400 hover:text-slate-600'}`}><X size={20} /></button>
              </div>

              <div className="max-h-[50vh] overflow-y-auto custom-scrollbar pr-2 space-y-4" style={{ direction: 'rtl' }}>
                <p className="text-sm font-bold opacity-70">اختر المنتجات التي ترغب باسترجاعها والصعوبة التي واجهتك معها:</p>
                {selectedOrder.items.map((item: any, idx: number) => {
                  const rIdx = returnItems.findIndex(r => r.id === (item.id || item.product?.id));
                  const rInfo = returnItems[rIdx];
                  if (!rInfo || item.isCancelled) return null;

                  return (
                    <div key={idx} className={`p-4 rounded-3xl border transition-all ${rInfo.selected ? 'border-orange-500 bg-orange-50 dark:bg-orange-500/10' : 'border-slate-100 dark:border-white/10'}`}>
                      <label className="flex items-center gap-4 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-5 h-5 accent-orange-500"
                          checked={rInfo.selected}
                          onChange={(e) => {
                            const newR = [...returnItems];
                            newR[rIdx].selected = e.target.checked;
                            setReturnItems(newR);
                          }}
                        />
                        <div className="flex-1 flex gap-3">
                          <img src={item.image} alt="" className="w-12 h-12 object-contain rounded-xl bg-white border" />
                          <div className="flex-1 py-1">
                            <p className="font-black text-sm">{item.name}</p>
                            <p className="text-xs font-bold opacity-50">{item.price} EGP</p>
                          </div>
                        </div>
                      </label>
                      {rInfo.selected && (
                        <div className="mt-4 mr-9">
                          <textarea
                            className={`w-full p-3 rounded-2xl border text-sm font-bold resize-none h-20 outline-none focus:border-orange-500 ${isDark ? 'bg-black/30 border-white/10 text-white' : 'bg-white border-slate-200'}`}
                            placeholder="ما سبب استرجاع المنتج؟ (تالف، غير مطابق، إلخ...)"
                            value={rInfo.reason}
                            onChange={(e) => {
                              const newR = [...returnItems];
                              newR[rIdx].reason = e.target.value;
                              setReturnItems(newR);
                            }}
                          ></textarea>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-4 pt-2">
                <button onClick={() => setReturnFormOpen(false)} className="flex-1 py-4 font-black rounded-2xl border border-slate-200 dark:border-white/10 opacity-70 hover:opacity-100">الغاء</button>
                <button onClick={handleSubmitReturn} disabled={isSubmittingReturn} className="flex-1 py-4 font-black rounded-2xl bg-orange-500 text-white flex items-center justify-center disabled:opacity-50">
                  {isSubmittingReturn ? <Loader2 className="animate-spin" size={20} /> : 'تأكيد الاسترجاع'}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {
        reviewFormOpen && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setReviewFormOpen(false)}></div>
            <div className={`relative p-8 rounded-[2.5rem] w-full max-w-[450px] space-y-6 ${isDark ? 'bg-[#1a1c22] text-white border border-white/10' : 'bg-white text-slate-800'} animate-scale-in`} style={{ direction: 'rtl' }}>
              <button onClick={() => setReviewFormOpen(false)} className="absolute top-6 left-6 p-2 rounded-full bg-slate-100 dark:bg-white/10 text-slate-500 dark:text-white/60"><X size={18} /></button>

              <div className="text-center space-y-2 pt-2">
                <div className="w-16 h-16 bg-blue-50 dark:bg-blue-500/10 text-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star size={32} className="fill-blue-500" />
                </div>
                <h3 className="text-2xl font-black">تقييم المنتج</h3>
                <p className="text-sm font-bold opacity-60 px-4">{reviewData.productName}</p>
              </div>

              <div className="flex justify-center gap-2 py-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setReviewData({ ...reviewData, rating: star })}
                    className="p-1 hover:scale-110 active:scale-90 transition-transform focus:outline-none"
                  >
                    <Star
                      size={40}
                      className={star <= reviewData.rating ? "fill-yellow-400 text-yellow-500" : "fill-transparent text-slate-300 dark:text-slate-600"}
                      strokeWidth={1.5}
                    />
                  </button>
                ))}
              </div>

              <div className="space-y-4">
                <textarea
                  className={`w-full p-4 rounded-2xl border text-sm font-bold resize-none h-32 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${isDark ? 'bg-black/30 border-white/10 text-white' : 'bg-slate-50 border-slate-200 text-slate-800'}`}
                  placeholder="أكتب رأيك وتجربتك للعملاء التانيين عن المنتج..."
                  value={reviewData.comment}
                  onChange={(e) => setReviewData({ ...reviewData, comment: e.target.value })}
                ></textarea>

                <label className="flex items-center gap-3 cursor-pointer py-2 px-1">
                  <input
                    type="checkbox"
                    className="w-5 h-5 accent-blue-500 rounded"
                    checked={reviewData.anonymous}
                    onChange={(e) => setReviewData({ ...reviewData, anonymous: e.target.checked })}
                  />
                  <span className="font-bold text-sm">إخفاء الهوية عند التقييم (فاعل خير)</span>
                </label>
              </div>

              <button onClick={handleSubmitReview} disabled={submittingReview} className="w-full py-4 rounded-2xl font-black bg-blue-600 text-white shadow-xl hover:bg-blue-700 active:scale-95 transition-all flex items-center justify-center disabled:opacity-50">
                {submittingReview ? <Loader2 className="animate-spin" size={24} /> : 'نشر التقييم'}
              </button>
            </div>
          </div>
        )
      }
    </>
  );
};
